import { partition } from 'lodash';
import {
  nodeTypes as getNodeTypes,
  allValidInputTypesForNodeType,
  modelTypes as getModelTypes,
  allValidInputTypesForModelType,
  type DataType,
  type NodeType,
  type ModelNodeType,
} from 'web';
import { HandleType } from '@/enums/handle-type.enum';
import { searchAndSort } from '@/utils/search';
import { MenuCategory, MenuData, ModelItem } from './nodes.types';

export const flattenMenuData = (menu: Partial<MenuData>): ModelItem[] => {
  let items: ModelItem[] = [];
  for (const key in menu) {
    const category = menu[key as keyof MenuData];
    if (category && 'children' in category && Array.isArray(category.children)) {
      items = items.concat(flattenMenuCategories([category]));
    }
  }
  // Deduplicate by id (keep first occurrence)
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

export const flattenMenuCategories = (categories: MenuCategory[]): ModelItem[] => {
  const result: ModelItem[] = [];
  for (const cat of categories) {
    const [withChildren, withoutChildren] = partition(cat.children, (child) => 'children' in child);
    result.push(...flattenMenuCategories(withChildren));
    result.push(...withoutChildren);
  }
  return result;
};

/**
 * Filters a list of ModelItem by a search string, matching displayName, description, or searchText (case-insensitive).
 * Uses enhanced search with relevance scoring for better results.
 */
export function filterNodesBySearch(items: ModelItem[], search: string): ModelItem[] {
  if (!search) return items;

  return searchAndSort(items, search, (item) => [item.displayName || '', item.searchText || '']);
}

export interface WasmFilterOverrides {
  wasmItemIds: Set<ModelItem['id']>;
  wasmKnownIds: Set<ModelItem['id']>;
}

/**
 * Filters a list of ModelItem by inputTypes/outputTypes and side of requested connection.
 * When `wasm` is provided, WASM-known items bypass backend types (used for input-type
 * filtering where backend data is unreliable, e.g. Crop declares "any" instead of
 * [image, mask, video]).
 */
export function filterNodesByInputOutputTypes(
  items: ModelItem[],
  typeKey: keyof Pick<ModelItem, 'inputTypes' | 'outputTypes'>,
  types: HandleType[],
  wasm?: WasmFilterOverrides,
): ModelItem[] {
  if (!items?.length) {
    return items;
  }

  // filter out items that don't have handles on the requested side e.g. preview and export
  const res = items.filter((item) => Boolean(item[typeKey] && item[typeKey].length));

  if (!types || !types.length) {
    return res;
  }

  if (types.length === 1 && types[0] === HandleType.KlingElement) {
    return res.filter((item) => types.every((type) => item[typeKey].includes(type)));
  }

  return res.filter((item) => {
    // If WASM knows this item, trust WASM as the source of truth
    if (wasm && wasm.wasmKnownIds.has(item.id)) {
      return wasm.wasmItemIds.has(item.id);
    }
    // For items WASM doesn't know (or when WASM is not used), fall back to backend types
    return item[typeKey].includes(HandleType.Any) || types.every((type) => item[typeKey].includes(type));
  });
}

/**
 * Returns the set of menu item IDs that can accept the given handle types,
 * using WASM as the source of truth for which node/model kinds accept which types.
 * This augments the backend's inputTypes which are unreliable (e.g., Crop declares "any"
 * instead of [image, mask, video] because the DB stores a single type per handle).
 */
export function getWasmItemIds(
  handleTypes: HandleType[],
  nodeKindById: Map<ModelItem['id'], NodeType>,
  modelKindById: Map<ModelItem['id'], ModelNodeType>,
): Set<ModelItem['id']> {
  const ids = new Set<ModelItem['id']>();
  for (const handleType of handleTypes) {
    const dataType = handleType as unknown as DataType;

    const compatibleNodeKinds = new Set<NodeType>(
      getNodeTypes().filter((nt) => allValidInputTypesForNodeType(nt).includes(dataType)),
    );
    const compatibleModelKinds = new Set<ModelNodeType>(
      getModelTypes().filter((mt) => allValidInputTypesForModelType(mt).includes(dataType)),
    );

    for (const [id, kind] of nodeKindById) {
      if (compatibleNodeKinds.has(kind)) ids.add(id);
    }
    for (const [id, kind] of modelKindById) {
      if (compatibleModelKinds.has(kind)) ids.add(id);
    }
  }
  return ids;
}
